import React, { useState, useEffect, useRef } from 'react';
import '../../styles/Fulfillment.css';
import TopMenu from '../../contexts/TopMenu';
import { API_BASE_URL } from '../../config';
import Modal from '../global/Modal';

function Tickets({ email, onLogout }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [isReplyLoading, setIsReplyLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const conversationRef = useRef(null);
  const fileInputRef = useRef(null);

  const fetchTickets = React.useCallback(async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      
      // Get auth credentials from localStorage
      const authKey = localStorage.getItem('authKey') || '';
      const userId = localStorage.getItem('userId') || '';
      
      if (!authKey || !userId) {
        throw new Error('Authentication credentials not found');
      }
      
      // Make request to tickets/list endpoint
      const response = await fetch(`${API_BASE_URL}/tickets/list`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          auth_key: authKey,
          user_id: userId,
          user_type: "brand",
          status: statusFilter || undefined,
          limit: 50,
          offset: 0
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch tickets');
      }
      
      setTickets(data.data.tickets || []);
    } catch (err) {
      setError(err.message || 'Failed to fetch tickets');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleTicketClick = async (ticket) => {
    try {
      setIsLoadingDetails(true);
      // Get auth credentials from localStorage
      const authKey = localStorage.getItem('authKey') || '';
      const userId = localStorage.getItem('userId') || '';
      
      if (!authKey || !userId) {
        throw new Error('Authentication credentials not found');
      }
      
      // Fetch detailed ticket information
      const response = await fetch(`${API_BASE_URL}/tickets/detail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          auth_key: authKey,
          user_id: userId,
          user_type: "brand",
          ticket_id: ticket._id
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch ticket details');
      }
      
      setSelectedTicket(data.data);
      setIsModalOpen(true);
      setReplyMessage('');
      setSelectedFile(null);
      setApiError('');
    } catch (error) {
      console.error('Error fetching ticket details:', error);
      setError(error.message || 'Failed to fetch ticket details');
      setTimeout(() => setError(''), 3000);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleSubmitReply = async (e) => {
    e.preventDefault();
    
    if (!replyMessage.trim() && !selectedFile) {
      setApiError('Please provide a message or attachment');
      return;
    }
    
    try {
      setIsReplyLoading(true);
      setApiError('');
      
      // Get auth credentials
      const authKey = localStorage.getItem('authKey') || '';
      const userId = localStorage.getItem('userId') || '';
      
      if (!authKey || !userId) {
        throw new Error('Authentication credentials not found');
      }

      // If there's an attachment, handle both attachment and message in one request
      if (selectedFile) {
        setIsUploading(true);
        // Pass the message to uploadAttachment
        await uploadAttachment(selectedFile, selectedTicket._id, userId, authKey, replyMessage.trim());
        setIsUploading(false);
      } else {
        // Only submit message if there's no attachment
        const response = await fetch(`${API_BASE_URL}/tickets/update/brand`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            auth_key: authKey,
            brand_id: userId,
            ticket_id: selectedTicket._id,
            update: {
              message: replyMessage.trim(),
              status: "in_progress" // Update status to in_progress when replying
            }
          })
        });
        
        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.message || 'Failed to submit reply');
        }
      }
      
      // Reset form and refresh ticket details
      setReplyMessage('');
      setSelectedFile(null);
      
      // Fetch updated ticket details
      await handleTicketClick(selectedTicket);
      
      // Also refresh tickets list in the background
      await fetchTickets(false);
    } catch (error) {
      console.error('Error replying to ticket:', error);
      setApiError(error.message || 'Failed to submit reply');
    } finally {
      setIsReplyLoading(false);
      setIsUploading(false);
    }
  };

  const uploadAttachment = async (file, ticketId, userId, authKey, message = '') => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('ticket_id', ticketId);
      formData.append('user_id', userId);
      formData.append('user_type', 'brand');
      formData.append('auth_key', authKey);
      
      // Add message if provided
      if (message) {
        formData.append('message', message);
      }
      
      const xhr = new XMLHttpRequest();
      
      // Setup progress tracking
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(progress);
        }
      });
      
      // Create a promise to handle the XHR response
      const uploadPromise = new Promise((resolve, reject) => {
        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            const response = JSON.parse(xhr.responseText);
            resolve(response.data.attachment_url);
          } else {
            reject(new Error('Upload failed: ' + xhr.statusText));
          }
        };
        
        xhr.onerror = function() {
          reject(new Error('Network error during upload'));
        };
      });
      
      // Send the request
      xhr.open('POST', `${API_BASE_URL}/tickets/brands/attachment/upload`, true);
      xhr.send(formData);
      
      return await uploadPromise;
    } catch (error) {
      console.error('Error uploading attachment:', error);
      throw new Error('Failed to upload attachment: ' + error.message);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setUploadProgress(0);
    }
  };

  const handleCloseTicket = async (ticket, e) => {
    if (e) {
      e.stopPropagation();
    }
    
    if (!window.confirm('Are you sure you want to close this ticket?')) {
      return;
    }
    
    // Ask for closing message
    const closingMessage = prompt('Please enter a closing message (optional):', 'This ticket has been resolved and closed.');
    
    // If user cancels the prompt, return early
    if (closingMessage === null) {
      return;
    }
    
    try {
      // Get auth credentials
      const authKey = localStorage.getItem('authKey') || '';
      const userId = localStorage.getItem('userId') || '';
      
      if (!authKey || !userId) {
        throw new Error('Authentication credentials not found');
      }
      var payload = {
        auth_key: authKey,
        user_id: userId,
        ticket_id: ticket._id,
        closing_message: closingMessage.trim() || undefined
      }
      // Close the ticket
      const response = await fetch(`${API_BASE_URL}/tickets/close/brand`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to close ticket');
      }
      
      // If modal is open, close it
      if (isModalOpen) {
        setIsModalOpen(false);
      }
      
      // Refresh tickets
      await fetchTickets(false);
    } catch (error) {
      console.error('Error closing ticket:', error);
      setError(error.message || 'Failed to close ticket');
      setTimeout(() => setError(''), 3000);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    let backgroundColor, color;
    
    switch (status) {
      case 'open':
        backgroundColor = '#fff3cd';
        color = '#856404';
        break;
      case 'in_progress':
        backgroundColor = '#cce5ff';
        color = '#004085';
        break;
      case 'resolved':
        backgroundColor = '#d4edda';
        color = '#155724';
        break;
      case 'closed':
        backgroundColor = '#f8f9fa';
        color = '#5a6268';
        break;
      default:
        backgroundColor = '#f8f9fa';
        color = '#5a6268';
    }
    
    return (
      <span style={{
        backgroundColor,
        color,
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '11px',
        fontWeight: '500',
        textTransform: 'capitalize',
        whiteSpace: 'nowrap'
      }}>
        {status.replace('_', ' ')}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    let backgroundColor, color;
    
    switch (priority) {
      case 'low':
        backgroundColor = '#f8f9fa';
        color = '#5a6268';
        break;
      case 'medium':
        backgroundColor = '#cce5ff';
        color = '#004085';
        break;
      case 'high':
        backgroundColor = '#fff3cd';
        color = '#856404';
        break;
      case 'urgent':
        backgroundColor = '#f8d7da';
        color = '#721c24';
        break;
      default:
        backgroundColor = '#f8f9fa';
        color = '#5a6268';
    }
    
    return (
      <span style={{
        backgroundColor,
        color,
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '11px',
        fontWeight: '500',
        textTransform: 'capitalize',
        whiteSpace: 'nowrap'
      }}>
        {priority}
      </span>
    );
  };

  // Add useEffect for auto-scrolling
  useEffect(() => {
    if (conversationRef.current && selectedTicket?.updates?.length > 0) {
      conversationRef.current.scrollTop = conversationRef.current.scrollHeight;
    }
  }, [selectedTicket?.updates]);

  if (loading) {
    return (
      <div className="fulfillment-loading">
        <div className="loader"></div>
        <p>Loading tickets...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fulfillment-error">
        <p>{error}</p>
        <button onClick={fetchTickets}>Retry</button>
      </div>
    );
  }

  return (
    <div className="dashboard-root">
      <TopMenu pageTitle="Support Tickets" email={email} onLogout={onLogout} />
      <div className="dashboard-container">
        <div className="fulfillment">
          <div className="fulfillment-header">
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">All Tickets</option>
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
                <button
                  onClick={() => fetchTickets()}
                  style={{
                    padding: '8px 15px',
                    background: '#fff',
                    color: '#000',
                    border: '1px solid #000',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Refresh
                </button>
              </div>
            </div>
          </div>
          
          <div className="orders-table">
            <table>
              <thead>
                <tr>
                  <th>Ticket ID</th>
                  <th>Date</th>
                  <th>Subject</th>
                  <th>Status</th>
                  <th>Priority</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tickets.length === 0 ? (
                  <tr>
                    <td colSpan="6">
                      <div style={{
                        padding: '60px 0',
                        textAlign: 'center',
                        fontFamily: 'Helvetica, Arial, sans-serif',
                      }}>
                        <div style={{
                          fontSize: '48px',
                          marginBottom: '20px',
                        }}>
                          🎫
                        </div>
                        <h3 style={{
                          fontSize: '20px',
                          fontWeight: '400',
                          margin: '0 0 10px 0',
                          letterSpacing: '0.5px',
                        }}>
                          No support tickets yet
                        </h3>
                        <p style={{
                          fontSize: '14px',
                          color: '#666',
                          fontWeight: '300',
                          maxWidth: '400px',
                          margin: '0 auto 20px',
                          lineHeight: '1.5',
                        }}>
                          When customers submit support requests, they'll appear here for you to respond.
                        </p>
                        <a 
                          href="/dashboard" 
                          style={{
                            padding: '8px 16px',
                            background: '#000',
                            color: '#fff',
                            textDecoration: 'none',
                            fontSize: '11px',
                            fontFamily: 'Helvetica, Arial, sans-serif',
                            letterSpacing: '1px',
                            textTransform: 'uppercase',
                            fontWeight: '400',
                            display: 'inline-block',
                            transition: 'all 0.2s ease',
                          }}
                        >
                          Go to Dashboard
                        </a>
                      </div>
                    </td>
                  </tr>
                ) : (
                  tickets
                    .sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0))
                    .map((ticket) => (
                      <tr key={ticket._id} onClick={() => handleTicketClick(ticket)}>
                        <td>{ticket._id.slice(-6).toUpperCase()}</td>
                        <td>{formatDate(ticket.created_at)}</td>
                        <td>{ticket.subject}</td>
                        <td>{getStatusBadge(ticket.status)}</td>
                        <td>{getPriorityBadge(ticket.priority)}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            {ticket.status !== 'closed' && (
                              <>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleTicketClick(ticket);
                                  }}
                                  style={{
                                    padding: '8px 15px',
                                    background: '#000',
                                    color: '#fff',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: '11px',
                                    fontFamily: 'Helvetica, Arial, sans-serif',
                                    letterSpacing: '1px',
                                    textTransform: 'uppercase',
                                    fontWeight: '400',
                                    transition: 'all 0.2s ease'
                                  }}
                                >
                                  Reply
                                </button>
                                
                                <button
                                  onClick={(e) => handleCloseTicket(ticket, e)}
                                  style={{
                                    padding: '8px 15px',
                                    background: '#fff',
                                    color: '#000',
                                    border: '1px solid #000',
                                    cursor: 'pointer',
                                    fontSize: '11px',
                                    fontFamily: 'Helvetica, Arial, sans-serif',
                                    letterSpacing: '1px',
                                    textTransform: 'uppercase',
                                    fontWeight: '400',
                                    transition: 'all 0.2s ease'
                                  }}
                                >
                                  Close
                                </button>
                              </>
                            )}
                            
                            {ticket.status === 'closed' && (
                              <span style={{
                                fontFamily: 'Helvetica, Arial, sans-serif',
                                fontSize: '13px',
                                color: '#666',
                                fontWeight: '300'
                              }}>
                                Closed
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>

          {isModalOpen && selectedTicket && (
            <Modal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              title={`Ticket #${selectedTicket._id.slice(-6).toUpperCase()} - ${selectedTicket.subject}`}
              size="large"
            >
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                height: '80vh',
                maxHeight: '700px'
              }}>
                {/* Ticket information header */}
                <div style={{
                  padding: '15px',
                  borderBottom: '1px solid #f0f0f0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                      <span style={{ fontWeight: '500', fontSize: '15px' }}>Status: {getStatusBadge(selectedTicket.status)}</span>
                      <span style={{ fontWeight: '500', fontSize: '15px' }}>Priority: {getPriorityBadge(selectedTicket.priority)}</span>
                    </div>
                    <div style={{ fontSize: '13px', color: '#555' }}>
                      {selectedTicket.category && <span>Category: {selectedTicket.category.replace('_', ' ')} | </span>}
                      <span>Created: {formatDate(selectedTicket.created_at)}</span>
                      {selectedTicket.order_id && <span> | Order: {selectedTicket.order_id}</span>}
                    </div>
                  </div>
                  
                  {selectedTicket.status !== 'closed' && (
                    <button
                      onClick={() => handleCloseTicket(selectedTicket)}
                      style={{
                        padding: '8px 15px',
                        background: '#fff',
                        color: '#000',
                        border: '1px solid #000',
                        cursor: 'pointer',
                        fontSize: '11px',
                        fontFamily: 'Helvetica, Arial, sans-serif',
                        letterSpacing: '1px',
                        textTransform: 'uppercase',
                        fontWeight: '400',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Close Ticket
                    </button>
                  )}
                </div>
                
                {/* WhatsApp style chat container */}
                <div 
                  style={{ 
                    flex: 1,
                    backgroundColor: '#f5f5f5',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  {/* Chat messages */}
                  <div 
                    ref={conversationRef}
                    style={{ 
                      flex: 1,
                      padding: '20px',
                      overflowY: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px'
                    }}
                  >
                    {isLoadingDetails ? (
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        height: '100%'
                      }}>
                        <div className="loader"></div>
                      </div>
                    ) : (
                      selectedTicket.updates && selectedTicket.updates.map((update, index) => {
                        const isCustomer = update.created_by === 'customer';
                        const prevUpdate = index > 0 ? selectedTicket.updates[index - 1] : null;
                        const prevDate = prevUpdate ? new Date(prevUpdate.created_at).toDateString() : null;
                        const currentDate = new Date(update.created_at).toDateString();
                        const showDateSeparator = !prevDate || prevDate !== currentDate;
                        
                        return (
                          <React.Fragment key={index}>
                            {showDateSeparator && (
                              <div style={{
                                textAlign: 'center',
                                margin: '10px 0',
                                fontSize: '12px',
                                color: '#666',
                                position: 'relative'
                              }}>
                                <span style={{
                                  backgroundColor: '#f5f5f5',
                                  padding: '0 10px',
                                  position: 'relative',
                                  zIndex: 1
                                }}>
                                  {new Date(update.created_at).toLocaleDateString('en-US', {
                                    weekday: 'long', 
                                    year: 'numeric', 
                                    month: 'long', 
                                    day: 'numeric'
                                  })}
                                </span>
                                <div style={{
                                  position: 'absolute',
                                  top: '50%',
                                  left: 0,
                                  right: 0,
                                  height: '1px',
                                  backgroundColor: '#e0e0e0',
                                  zIndex: 0
                                }}></div>
                              </div>
                            )}
                            
                            <div style={{
                              alignSelf: isCustomer ? 'flex-start' : 'flex-end',
                              maxWidth: '70%',
                              position: 'relative'
                            }}>
                              <div style={{
                                backgroundColor: isCustomer ? '#ffffff' : '#000000',
                                color: isCustomer ? '#000000' : '#ffffff',
                                padding: '12px',
                                borderRadius: '12px',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                position: 'relative',
                                marginBottom: '2px'
                              }}>
                                <div style={{
                                  fontSize: '14px',
                                  lineHeight: '1.5',
                                  whiteSpace: 'pre-wrap'
                                }}>
                                  {update.message}
                                </div>
                                
                                {update.attachment_urls && update.attachment_urls.length > 0 && (
                                  <div style={{ marginTop: '10px' }}>
                                    <div style={{ 
                                      display: 'flex', 
                                      flexWrap: 'wrap', 
                                      gap: '10px' 
                                    }}>
                                      {update.attachment_urls.map((url, idx) => {
                                        const isImage = url.match(/\.(jpg|jpeg|png|gif|webp)$/i);
                                        return (
                                          <div key={idx}>
                                            {isImage ? (
                                              <div style={{ 
                                                position: 'relative'
                                              }}>
                                                <img 
                                                  src={url} 
                                                  alt={`Attachment ${idx + 1}`}
                                                  style={{
                                                    maxWidth: '200px',
                                                    maxHeight: '200px',
                                                    borderRadius: '8px',
                                                    cursor: 'pointer',
                                                    border: isCustomer ? '1px solid #eee' : '1px solid #333'
                                                  }}
                                                  onClick={() => window.open(url, '_blank')}
                                                />
                                              </div>
                                            ) : (
                                              <a 
                                                href={url} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                style={{
                                                  display: 'inline-block',
                                                  padding: '8px 12px',
                                                  backgroundColor: isCustomer ? '#f0f0f0' : '#333',
                                                  color: isCustomer ? '#333' : '#fff',
                                                  borderRadius: '8px',
                                                  fontSize: '13px',
                                                  textDecoration: 'none',
                                                  marginTop: '5px'
                                                }}
                                              >
                                                📎 Attachment {idx + 1}
                                              </a>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                                
                                {update.is_closing_message && (
                                  <div style={{
                                    backgroundColor: isCustomer ? '#fcf8e3' : '#664d0330',
                                    color: isCustomer ? '#8a6d3b' : '#ffe0b2',
                                    padding: '6px 10px',
                                    borderRadius: '8px',
                                    marginTop: '10px',
                                    fontSize: '12px'
                                  }}>
                                    Ticket closed with this message
                                  </div>
                                )}
                              </div>
                              
                              <div style={{
                                fontSize: '11px',
                                color: '#808080',
                                textAlign: isCustomer ? 'left' : 'right',
                                marginTop: '4px',
                                paddingLeft: isCustomer ? '8px' : '0',
                                paddingRight: isCustomer ? '0' : '8px'
                              }}>
                                <span style={{ fontWeight: '500' }}>
                                  {isCustomer ? 'Customer' : 'Support'}
                                </span> · {formatTime(update.created_at)}
                              </div>
                            </div>
                          </React.Fragment>
                        );
                      })
                    )}
                  </div>
                  
                  {/* Reply input section - only show if ticket is not closed */}
                  {selectedTicket.status !== 'closed' && (
                    <div style={{
                      borderTop: '1px solid #e0e0e0',
                      backgroundColor: '#fff',
                      padding: '15px',
                      position: 'sticky',
                      bottom: 0
                    }}>
                      <form onSubmit={handleSubmitReply} style={{ display: 'flex', flexDirection: 'column' }}>
                        {apiError && (
                          <div style={{
                            color: '#d32f2f',
                            marginBottom: '10px',
                            padding: '8px',
                            backgroundColor: '#fff3f3',
                            borderRadius: '4px',
                            fontSize: '13px'
                          }}>
                            {apiError}
                          </div>
                        )}
                        
                        {selectedFile && (
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '8px 12px',
                            backgroundColor: '#f7f7f7',
                            borderRadius: '8px',
                            marginBottom: '10px'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <span>📎</span>
                              <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {selectedFile.name}
                              </div>
                              {isUploading && (
                                <div style={{ fontSize: '12px', color: '#666' }}>
                                  Uploading: {uploadProgress}%
                                </div>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => setSelectedFile(null)}
                              style={{ 
                                background: 'none', 
                                border: 'none', 
                                cursor: 'pointer',
                                fontSize: '14px',
                                color: '#666'
                              }}
                            >
                              ✕
                            </button>
                          </div>
                        )}

                        <div style={{ display: 'flex', gap: '10px' }}>
                          <textarea
                            value={replyMessage}
                            onChange={(e) => setReplyMessage(e.target.value)}
                            placeholder="Type a message..."
                            style={{
                              flex: 1,
                              padding: '12px 15px',
                              border: '1px solid #e0e0e0',
                              borderRadius: '24px',
                              fontSize: '14px',
                              resize: 'none',
                              outline: 'none',
                              minHeight: '24px',
                              maxHeight: '100px',
                              fontFamily: 'Helvetica, Arial, sans-serif'
                            }}
                            rows={1}
                            onInput={(e) => {
                              // Auto-resize the textarea
                              e.target.style.height = 'auto';
                              e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
                            }}
                          />
                          
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                            <button
                              type="button"
                              onClick={() => fileInputRef.current.click()}
                              disabled={isReplyLoading || isUploading}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: (isReplyLoading || isUploading) ? 'not-allowed' : 'pointer',
                                padding: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '20px'
                              }}
                              title="Attach file"
                            >
                              📎
                              <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileSelect}
                                style={{ display: 'none' }}
                              />
                            </button>
                            
                            <button
                              type="submit"
                              disabled={isReplyLoading || isUploading || (!replyMessage.trim() && !selectedFile)}
                              style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                background: '#000',
                                color: '#fff',
                                border: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: (isReplyLoading || isUploading || (!replyMessage.trim() && !selectedFile)) ? 'not-allowed' : 'pointer',
                                fontSize: '16px'
                              }}
                            >
                              {isReplyLoading || isUploading ? (
                                <span style={{ 
                                  width: '14px', 
                                  height: '14px', 
                                  border: '2px solid rgba(255,255,255,0.3)', 
                                  borderTop: '2px solid #fff',
                                  borderRadius: '50%',
                                  animation: 'spin 1s linear infinite',
                                  display: 'inline-block'
                                }}></span>
                              ) : '➤'}
                            </button>
                          </div>
                        </div>
                      </form>
                    </div>
                  )}
                  
                  {/* Closed ticket message */}
                  {selectedTicket.status === 'closed' && (
                    <div style={{
                      padding: '15px',
                      backgroundColor: '#f8f9fa',
                      borderTop: '1px solid #e0e0e0',
                      textAlign: 'center',
                      color: '#5a6268',
                      fontSize: '14px'
                    }}>
                      This ticket is closed. No further replies can be sent.
                    </div>
                  )}
                </div>
              </div>
            </Modal>
          )}
        </div>
      </div>
    </div>
  );
}

export default Tickets; 